import { z } from 'zod';
import { zAdminComment } from './admin';
import type { GameId } from './common';

/**
 * «乐游 Jackpot» — the shared progressive jackpot (docs/superpowers/specs/2026-10-01-jackpot-design.md).
 * Amounts are whole tokens; `poolBefore` / `contribution` of a draw proof are units (1 token = 10 000 units).
 */

/** Everything needed to recompute one draw with `verifyJackpot` (@tg/games/jackpot). */
export interface JackpotProof {
  /** the stake's bet */
  betId: string;
  game: GameId;
  /** pool before the stake, units */
  poolBefore: number;
  /** contribution added (cut by the ceiling), units */
  contribution: number;
  /** ceiling at the time of the draw, tokens */
  cap: number;
  /** uint32 roll of the jackpot stream */
  roll: number;
  won: boolean;
  /** tokens paid (0 unless won) */
  payout: number;
  /** jackpot cycle number (1, 2, …) */
  cycle: number;
  serverSeedHash: string;
  /** revealed seed (null until the seed pair is rotated / the round has ended) */
  serverSeed: string | null;
  /** the jackpot stream's clientSeed (shared rounds: `round:<roundId>:<publicId>:<idempotencyKey>`) */
  clientSeed: string;
  nonce: number;
  at: string;
}

export interface JackpotWinner {
  nickname: string;
  publicId: number;
  avatar: string;
  game: GameId;
  amount: number;
  at: string;
  proof: JackpotProof;
}

/** GET /api/jackpot */
export interface JackpotResponse {
  enabled: boolean;
  /**
   * why `enabled` is false: `off` — JACKPOT_ENABLED=0; `region` — the request comes from a country of
   * JACKPOT_BLOCKED_COUNTRIES (then every number is 0 and `winners` is empty: the jackpot does not exist there)
   */
  reason?: 'off' | 'region';
  /** current pool, whole tokens */
  pool: number;
  cap: number;
  seed: number;
  contributionBps: number;
  cycle: number;
  /** the last 10 winners, newest first */
  winners: JackpotWinner[];
}

/** GET /api/jackpot/draws/:betId (the bet's owner) */
export interface JackpotDrawResponse {
  /** null: the stake had no draw (refunded, zero stake, jackpot off, not processed yet) */
  draw: JackpotProof | null;
}

export interface JackpotUpdateEvent {
  pool: number;
}

export interface JackpotWonEvent {
  nickname: string;
  publicId: number;
  amount: number;
  game: GameId;
  at: string;
}

export interface JackpotServerToClient {
  /** the pool changed (coalesced: at most once per second) */
  'jackpot:update': (e: JackpotUpdateEvent) => void;
  /** somebody hit the jackpot (to everyone) */
  'jackpot:won': (e: JackpotWonEvent) => void;
}

export const JACKPOT_MAX_BPS = 500;
export const JACKPOT_MAX_CAP = 8_888_888;

/** PUT /api/admin/jackpot */
export const zAdminJackpot = z
  .object({
    contributionBps: z.number().int().min(0).max(JACKPOT_MAX_BPS),
    seed: z.number().int().min(1).max(JACKPOT_MAX_CAP - 1),
    cap: z.number().int().min(2).max(JACKPOT_MAX_CAP),
    comment: zAdminComment,
  })
  .strict()
  .refine((v) => v.seed < v.cap, { message: 'seed must be below cap', path: ['seed'] });
export type AdminJackpotInput = z.infer<typeof zAdminJackpot>;

/** GET /api/admin/jackpot */
export interface AdminJackpotState extends Omit<JackpotResponse, 'winners'> {
  /** ISO-2 countries without the jackpot (JACKPOT_BLOCKED_COUNTRIES), sorted; empty — none */
  blockedCountries: string[];
  /** pool in units (exact) */
  units: number;
  /** Σ of the house `jackpot` account — equals the pool in whole tokens */
  account: number;
  /** stakes waiting for their draw */
  pending: number;
  /** total paid to winners, tokens */
  paid: number;
  /** wins, newest first (up to 50) */
  wins: JackpotWinner[];
}
